import { useState } from "react";
import { MapPin, Phone, Mail, Clock, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { GoogleReviewsBadge, WriteReviewButton } from "@/components/GoogleReviewsBadge";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_BG = "https://images.unsplash.com/photo-1704457031745-fe39ed2d296c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const services = [
  "Landscaping Design",
  "Cutting Grass",
  "Trimming Edges",
  "Fertilizers",
  "Edging",
  "Grooming",
  "Sod & Soil",
  "Maintenance",
  "Mulching",
  "Planting",
  "Seasonal Cleanup",
  "Snow Removal & Salting",
  "Fence Staining",
  "Deck Cleaning & Driveway Sealing",
  "General Inquiry",
];

const contactInfo = [
  {
    icon: MapPin,
    title: "Address",
    lines: ["2 Robert Speck Pkwy", "L4Z 1H8 Mississauga, ON"],
  },
  {
    icon: Phone,
    title: "Phone",
    lines: ["+1 647.242.2860"],
    link: "tel:+16472422860",
  },
  {
    icon: Mail,
    title: "Email",
    lines: ["info@absolutelandscapers.ca"],
    link: "mailto:info@absolutelandscapers.ca",
  },
  {
    icon: Clock,
    title: "Hours",
    lines: ["Mon – Sun: 8:00 AM – 6:00 PM"],
  },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", service: "", message: ""
  });
  const [status, setStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [serviceValue, setServiceValue] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, { ...form, service: serviceValue });
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
      setServiceValue("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page">
      {/* Hero */}
      <section
        className="relative min-h-[40vh] flex items-center justify-center overflow-hidden"
        style={{ paddingTop: "80px" }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('${HERO_BG}')` }}
        />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 text-center px-4 py-14">
          <span
            className="inline-block mb-3 px-4 py-1.5 bg-[#8dc63f]/20 text-[#8dc63f] text-xs font-semibold rounded-full uppercase tracking-widest border border-[#8dc63f]/30"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Free Estimates
          </span>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-3"
            style={{ fontFamily: "'Outfit', sans-serif", textShadow: "0 2px 20px rgba(0,0,0,0.4)" }}
          >
            Contact Us
          </h1>
          <p className="text-white/80 text-base mt-4 max-w-xl mx-auto">
            Reach out for a free, no-obligation estimate on any of our services
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 md:py-28 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Contact Info (left) */}
            <div className="lg:col-span-2">
              <span
                className="uppercase tracking-[0.2em] text-xs font-semibold text-[#3a5c2e] block mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Get in Touch
              </span>
              <h2
                className="text-2xl sm:text-3xl font-bold text-[#1F291E] mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                We'd Love to Hear From You
              </h2>
              <p className="text-[#4A5D48] text-sm leading-relaxed mb-8">
                Have a question about our services or ready to schedule your free estimate? Fill out the form or reach us directly — we respond within 24 hours.
              </p>

              <div className="space-y-5">
                {contactInfo.map((info) => (
                  <div
                    key={info.title}
                    data-testid={`contact-info-${info.title.toLowerCase()}`}
                    className="flex items-start gap-4"
                  >
                    <div className="w-10 h-10 rounded-xl bg-[#F0F4EF] flex items-center justify-center shrink-0">
                      <info.icon size={18} color="#3a5c2e" />
                    </div>
                    <div>
                      <p
                        className="font-semibold text-[#1F291E] text-sm mb-0.5"
                        style={{ fontFamily: "'Outfit', sans-serif" }}
                      >
                        {info.title}
                      </p>
                      {info.lines.map((line, i) =>
                        info.link && i === 0 ? (
                          <a
                            key={i}
                            href={info.link}
                            className="text-[#3a5c2e] text-sm hover:underline"
                          >
                            {line}
                          </a>
                        ) : (
                          <p key={i} className="text-[#4A5D48] text-sm">{line}</p>
                        )
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map placeholder */}
              <div className="mt-8 rounded-2xl overflow-hidden border border-[#E2E8DF]">
                <iframe
                  title="Absolute Landscapers Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2886.123456789!2d-79.649!3d43.604!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x882b46e87bf08d7f%3A0x0!2s2+Robert+Speck+Pkwy%2C+Mississauga%2C+ON+L4Z+1H8!5e0!3m2!1sen!2sca!4v1234567890"
                  width="100%"
                  height="200"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="contact-map"
                />
              </div>

              {/* Google Reviews trust badge */}
              <div className="mt-6 p-4 bg-[#F0F4EF] rounded-xl border border-[#E2E8DF]">
                <p className="text-[#4A5D48] text-xs mb-3 font-medium uppercase tracking-wide">
                  Trusted on Google
                </p>
                <div className="flex flex-col gap-2">
                  <GoogleReviewsBadge />
                  <WriteReviewButton className="w-full justify-center" />
                </div>
              </div>
            </div>

            {/* Form (right) */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-[#E2E8DF] p-8 shadow-sm">
                <h3
                  className="text-xl font-bold text-[#1F291E] mb-2"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Request a Free Quote
                </h3>
                <p className="text-[#4A5D48] text-sm mb-7">
                  Fill out the form below and we'll get back to you within 24 hours.
                </p>

                {/* Success */}
                {status === "success" && (
                  <div
                    data-testid="contact-success"
                    className="mb-6 flex items-start gap-3 bg-[#F0F4EF] border border-[#8dc63f]/40 rounded-xl p-4"
                  >
                    <CheckCircle2 size={20} className="text-[#3a5c2e] shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-[#1F291E] text-sm">Message Sent!</p>
                      <p className="text-[#4A5D48] text-sm mt-0.5">
                        Thank you for reaching out. We'll contact you within 24 hours.
                      </p>
                    </div>
                  </div>
                )}

                {/* Error */}
                {status === "error" && (
                  <div
                    data-testid="contact-error"
                    className="mb-6 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4"
                  >
                    <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-red-700 text-sm">Something went wrong. Please try again or call us directly.</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Name */}
                    <div>
                      <label className="block text-[#1F291E] text-sm font-medium mb-1.5" htmlFor="name">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        data-testid="contact-name-input"
                        className="w-full rounded-lg border border-[#E2E8DF] bg-[#F9F8F6] px-4 py-2.5 text-sm text-[#1F291E] placeholder-[#9aab97] focus:outline-none focus:ring-2 focus:ring-[#3a5c2e]/30 focus:border-[#3a5c2e] transition-all"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-[#1F291E] text-sm font-medium mb-1.5" htmlFor="email">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                        className="w-full rounded-lg border border-[#E2E8DF] bg-[#F9F8F6] px-4 py-2.5 text-sm text-[#1F291E] placeholder-[#9aab97] focus:outline-none focus:ring-2 focus:ring-[#3a5c2e]/30 focus:border-[#3a5c2e] transition-all"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Phone */}
                    <div>
                      <label className="block text-[#1F291E] text-sm font-medium mb-1.5" htmlFor="phone">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="(647) 000-0000"
                        data-testid="contact-phone-input"
                        className="w-full rounded-lg border border-[#E2E8DF] bg-[#F9F8F6] px-4 py-2.5 text-sm text-[#1F291E] placeholder-[#9aab97] focus:outline-none focus:ring-2 focus:ring-[#3a5c2e]/30 focus:border-[#3a5c2e] transition-all"
                      />
                    </div>

                    {/* Service */}
                    <div>
                      <label className="block text-[#1F291E] text-sm font-medium mb-1.5" htmlFor="service">
                        Service Required
                      </label>
                      <Select value={serviceValue} onValueChange={setServiceValue}>
                        <SelectTrigger
                          data-testid="contact-service-select"
                          className="w-full rounded-lg border-[#E2E8DF] bg-[#F9F8F6] px-4 h-[42px] text-sm text-[#1F291E] focus:ring-[#3a5c2e]/30 focus:ring-2"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (
                            <SelectItem key={s} value={s} className="text-sm">
                              {s}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-[#1F291E] text-sm font-medium mb-1.5" htmlFor="message">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      placeholder="Tell us about your project, property size, and any specific requirements..."
                      data-testid="contact-message-input"
                      className="w-full rounded-lg border border-[#E2E8DF] bg-[#F9F8F6] px-4 py-3 text-sm text-[#1F291E] placeholder-[#9aab97] resize-none focus:outline-none focus:ring-2 focus:ring-[#3a5c2e]/30 focus:border-[#3a5c2e] transition-all"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit-btn"
                    className="btn-primary w-full py-3.5 rounded-xl text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? "Sending..." : "Send Quote Request"}
                  </button>
                  <p className="text-[#9aab97] text-xs text-center">
                    We respond within 24 hours. Your information is kept private.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
